import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import {
  ColGroup,
  TableBody,
  TableHead,
  CustomPanel,
  CustomDynamicResultTable,
  FormattedDate,
  entitlementUtils,
  CustomSearch,
  CustomFilterWrapper,
  filterField,
  DateFilter,
  useFeatureToggle,
} from '@css-xp/base';
import { fetchOrdersForTransactionAccount } from './queries/fetch-orders-for-transaction-account';
import {
  DEFAULT_ROWS_PER_PAGE,
  getPurchaseMetadataEntries,
  NO_DETAILS_TO_SHOW,
} from '@atlassian/cssxp-utils-entitlement';
import { generatedTypesNew } from '@css-xp/graphtypes';
import { CONTACT_PATH_2 } from '@atlassian/cssxp-typescommon';
import './order-history-for-transaction-account.scss';
import { ENABLE_AAID_FOR_CONTACT_PAGE_REDIRECTION } from '@atlassian/cssxp-utils-generic';

interface FormData {
  searchTerm: string;
}

const OrderHistoryTxA = ({
  id,
  label,
  shouldForceEnableFilters = false,
}: {
  id: string;
  label: string;
  shouldForceEnableFilters?: boolean;
}) => {
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterOptions, setFilterOptions] = useState<generatedTypesNew.FilterOption[]>([]);
  const [searchBarOptions, setSearchBarOptions] = useState<generatedTypesNew.FilterOption[]>([]);
  const [filterFields, setFilterFields] = useState<filterField[]>([]);
  const [dateRangeOptions, setDateRangeOptions] = useState<generatedTypesNew.FilterOption[]>([]);
  const [isEnabled] = useFeatureToggle();
  const panelHeading = 'order history for this billing account';
  const emptyStateProps = {
    header: '',
    description: NO_DETAILS_TO_SHOW,
  };
  const columns = [
    {
      name: 'Subscription',
    },
    {
      name: 'Date',
    },
    {
      name: 'Notes',
      customClassNames: 'col-20',
    },
    {
      name: 'Support ticket',
      customClassNames: 'col-10',
    },
    {
      name: 'Details',
      customClassNames: 'col-20',
    },
    {
      name: 'Placed by',
    },
  ];
  const defaultSearchOptions = useMemo(
    () => ({
      first: DEFAULT_ROWS_PER_PAGE,
      offset: 0,
      sort: {
        sortFields: ['createdDate', 'id'],
        sortFieldsDirections: [generatedTypesNew.SortDirection.DESC, generatedTypesNew.SortDirection.DESC],
      },
    }),
    []
  );
  const { data, loading, error, refetch } = useQuery<
    generatedTypesNew.FetchOrdersByTransactionAccountIdQuery,
    generatedTypesNew.FetchOrdersByTransactionAccountIdQueryVariables
  >(fetchOrdersForTransactionAccount, {
    variables: {
      id,
      searchOptions: defaultSearchOptions,
    },
    displayName: 'fetchOrdersForTransactionAccount',
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const billingAdminsList = data?.transactionAccount?.billingAdmins?.nodes || [];
    if (billingAdminsList.length > 0) {
      const billingAdmins = billingAdminsList.map((billingAdmin) => ({
        key: billingAdmin?.email,
        value: billingAdmin?.email,
      }));

      setFilterFields([
        {
          filterItem: 'Contact',
          queryKey: 'billingContactEmail',
          iterableValues: billingAdmins as Array<{ key: string; value: string }>,
        },
      ]);
    }
  }, [data]);

  const nodes = data?.transactionAccount?.orders?.nodes ?? [];
  const totalCount = data?.transactionAccount?.orders?.totalCount ?? 0;

  const onSearch = useCallback(
    async (formData: FormData) => {
      const currentSearchOptions = [];
      const searchInput = formData?.searchTerm;
      if (searchInput) {
        currentSearchOptions.push({
          key: 'orderLineItemsEntitlementNumber',
          value: searchInput,
        });
      }
      setSearchBarOptions(currentSearchOptions);
      setCurrentPage(1);
      refetch({
        filterOptions: [...filterOptions, ...currentSearchOptions, ...dateRangeOptions],
        searchOptions: defaultSearchOptions,
      });
    },
    [refetch, filterOptions, defaultSearchOptions, dateRangeOptions]
  );

  const onFilter = useCallback(
    (selectedFilters: generatedTypesNew.FilterOption[]) => {
      setFilterOptions(selectedFilters);
      setCurrentPage(1);
      refetch({
        filterOptions: [...searchBarOptions, ...selectedFilters, ...dateRangeOptions],
        searchOptions: defaultSearchOptions,
      });
    },
    [refetch, searchBarOptions, defaultSearchOptions, dateRangeOptions]
  );

  const onDateRangeChange = useCallback(
    (value: generatedTypesNew.DateRange) => {
      const currentDateRangeOptions = [
        {
          key: 'createdDateIsGte',
          value: value.from,
        },
        {
          key: 'createdDateIsLte',
          value: value.to,
        },
      ];
      setDateRangeOptions(currentDateRangeOptions);
      setCurrentPage(1);
      refetch({
        filterOptions: [...searchBarOptions, ...currentDateRangeOptions, ...filterOptions],
        searchOptions: defaultSearchOptions,
      });
    },
    [refetch, defaultSearchOptions, searchBarOptions, filterOptions]
  );

  const onPageChange = useCallback(
    (pageNumber: number) => {
      setCurrentPage(pageNumber);
      refetch({
        searchOptions: {
          ...defaultSearchOptions,
          offset: (pageNumber - 1) * rowsPerPage,
          first: rowsPerPage,
        },
        filterOptions: [...searchBarOptions, ...filterOptions, ...dateRangeOptions],
      });
    },
    [rowsPerPage, filterOptions, searchBarOptions, dateRangeOptions, defaultSearchOptions, refetch]
  );

  const handleRowsChange = useCallback(
    (newRowPerPage: number) => {
      if (newRowPerPage === rowsPerPage) {
        return;
      }
      setCurrentPage(1);
      setRowsPerPage(newRowPerPage);
      refetch({
        searchOptions: {
          ...defaultSearchOptions,
          first: newRowPerPage,
        },
        filterOptions: [...searchBarOptions, ...filterOptions, ...dateRangeOptions],
      });
    },
    [rowsPerPage, filterOptions, searchBarOptions, dateRangeOptions, defaultSearchOptions, refetch]
  );

  const filters = (
    <div className="order-history-table-txa-filter">
      <CustomSearch placeholder={'Search all orders'} onSearch={onSearch} isLoading={loading} />
      <DateFilter dateValue={onDateRangeChange} />
      <CustomFilterWrapper filterFields={filterFields} handleFilterUpdate={onFilter} />
    </div>
  );

  return (
    <CustomPanel
      testId="order-history-txa"
      heading={label}
      expandedHeader={`Collapse to hide ${panelHeading}`}
      collapsedHeader={`Expand to view ${panelHeading}`}
    >
      <CustomDynamicResultTable
        className="order-history-table-txa"
        testId="order-history-txa"
        isLoading={loading}
        error={error?.toString()}
        noResults={nodes.length < 1}
        emptyStateProps={emptyStateProps}
        filters={filters}
        totalCount={totalCount}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        isPaginationEnabled={nodes.length > 0 && totalCount > DEFAULT_ROWS_PER_PAGE}
        onRowChange={handleRowsChange}
        boxShadow={false}
        shouldForceEnableFilters={shouldForceEnableFilters}
      >
        <ColGroup columns={columns} />
        <TableHead columns={columns} />
        <TableBody>
          {nodes.map((node, index) => {
            const orderLineItems = node?.orderLineItems?.nodes ?? [];
            return (
              <>
                <tr
                  key={`order-history-txa-${node?.id}`}
                  data-testid={`order-history-txa-parentrow-${index}`}
                  className="grey-row"
                >
                  <td colSpan={6}>
                    <h2>Order number: {node?.orderNumber}</h2>
                  </td>
                </tr>
                {orderLineItems.map((lineItem, childIndex) => {
                  const purchaseMetadata = getPurchaseMetadataEntries(lineItem?.orderLineItemsPurchaseMetadata);
                  return (
                    <tr
                      key={`order-history-txa-${lineItem?.lineItemId}`}
                      data-testid={`order-history-txa-childrow-${childIndex}`}
                      className="white-row"
                    >
                      <td>
                        <entitlementUtils.CloudProductDetailView
                          productId={lineItem?.productId as string}
                          entitlementNumber={lineItem?.entitlementNumber || ''}
                          edition={lineItem?.edition}
                          productName={lineItem?.productName}
                        />
                      </td>
                      <td>
                        <FormattedDate date={node?.created} showTime />
                      </td>
                      <td>
                        <span className="line-clamp">{purchaseMetadata?.note}</span>
                      </td>
                      <td>{node?.metadata?.supportTicketNumber}</td>
                      <td>
                        <entitlementUtils.OrderDetailView orderLineItem={lineItem} />
                      </td>
                      <td>
                        {node?.contextSubject ? (
                          <Link
                            to={`${CONTACT_PATH_2}/${
                              isEnabled(ENABLE_AAID_FOR_CONTACT_PAGE_REDIRECTION) && node?.contextSubjectId
                                ? node.contextSubjectId
                                : window.btoa(node?.contextSubject)
                            }`}
                          >
                            {node?.contextSubject}
                          </Link>
                        ) : (
                          node?.contextSubject
                        )}
                      </td>
                    </tr>
                  );
                })}
              </>
            );
          })}
        </TableBody>
      </CustomDynamicResultTable>
    </CustomPanel>
  );
};

export default OrderHistoryTxA;
